"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { GROQButton } from "@/sanity/lib/definitions";
import { stegaClean } from "@sanity/client/stega";
import { Slot } from "@radix-ui/react-slot";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";
import { useCTAWrapper } from "@/hooks/useCTAWrapper";
import { iconMap } from "@/lib/iconMap";

const buttonVariants = cva("button", {
  variants: {
    variant: {
      default: "cta button-default",
      thickBorder: "cta-thick-border button-thick-border",
      ghost: "button-ghost",
      transparent: "button-transparent",
      borders: "button-borders",
      darkGreen: "button-dark-green",
      lightGreen: "button-light-green",
      white: "button-white",
    },
    size: {
      default: "button-size-default",
      sm: "button-size-small",
      lg: "button-size-large",
    },
    shape: {
      round: "button-shape-round",
      pill: "button-shape-pill",
      square: "button-shape-square",
    },
    action: {
      link: "",
      popup: "",
      anchor: "",
      videoPopup: "",
      none: "",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "lg",
    shape: "pill",
    action: "none",
  },
});

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, shape, action, size, asChild = false, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const children = props.children;
    return (
      <Comp
        className={cn(
          "group",
          buttonVariants({ variant, size, shape, className }),
        )}
        data-type={variant}
        data-size={size}
        data-shape={shape}
        data-action={action}
        ref={ref}
        {...props}
      >
        {children}
        <span></span>
      </Comp>
    );
  },
);
Button.displayName = "Button";

export interface CTAButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  data: GROQButton;
}

const CTAButton = React.forwardRef<HTMLButtonElement, CTAButtonProps>(
  ({ className, data, ...props }, ref) => {
    const { title } = useCTAWrapper(data?.cta);
    const cleanedData = stegaClean(data ?? {}) ?? null;

    const type = cleanedData?.type ?? "default";
    const size = cleanedData?.size ?? "default";
    const shape = cleanedData?.shape ?? "round";

    const hasIcon = cleanedData?.cta?.icon ?? "none";
    const icon = iconMap[hasIcon as keyof typeof iconMap];

    const action = cleanedData?.cta?.action ?? null;

    return (
      <CTAWrapper
        cta={cleanedData?.cta}
        className={cn(
          "inline-block mx-[.5rem] w-[calc(100%-1rem)] min-w-[37.25rem] sm:w-auto relative z-10",
          className,
        )}
      >
        <Button
          className="relative w-full group"
          variant={type}
          size={size}
          shape={shape}
          action={action ?? "none"}
          ref={ref}
          {...props}
        >
          {icon && (
            <div className="button-icon relative overflow-clip transition-all duration-300 !w-[4rem] !min-w-[4rem] !h-[3rem] me-[1rem]">
              {icon}
            </div>
          )}
          <div className="relative transition-all duration-200 button-text overflow-clip">
            {data?.cta
              ? (title ?? "Please add a title")
              : "Please select a CTA"}
          </div>
        </Button>
      </CTAWrapper>
    );
  },
);
CTAButton.displayName = "CTAButton";

export { Button, buttonVariants, CTAButton };
